import { useEffect, useRef } from "react";

export const computeReorder = (
  currentOrder,
  draggedIndex,
  slotIndex,
  pinnedColumns
) => {
  const newOrder = [...currentOrder];
  const draggedField = newOrder[draggedIndex];
  newOrder.splice(draggedIndex, 1);
  const finalSlotIndex = draggedIndex < slotIndex ? slotIndex - 1 : slotIndex;
  newOrder.splice(finalSlotIndex, 0, draggedField);
  const pinnedCount = pinnedColumns.length;
  const wasIn = draggedIndex < pinnedCount;
  const isIn = finalSlotIndex < pinnedCount;
  const newCount =
    wasIn && !isIn
      ? pinnedCount - 1
      : isIn && !wasIn
      ? pinnedCount + 1
      : pinnedCount;
  return { newOrder, newPinned: newOrder.slice(0, newCount) };
};

const DND_ATTACHED_KEY = "__dndAttached";
const COLUMN_HEADER_SELECTOR = ".MuiDataGrid-columnHeader[data-field]";
const INDICATOR_CLASS_LEFT = "dnd-indicator-left";
const INDICATOR_CLASS_RIGHT = "dnd-indicator-right";
const SCROLL_EDGE_ZONE = 40;
const SCROLL_SPEED = 8;

export const useColumnDragDrop = ({
  containerRef,
  columnOrder,
  onReorder,
  pinnedColumns = [],
  setPinnedColumns,
  onPinnedColumnsChange,
}) => {
  const dragStartIndexRef = useRef(-1);
  const slotIndexRef = useRef(-1);
  const indicatorParentElementRef = useRef(null);
  const doubleClickDragModeRef = useRef(false);
  const doubleClickHeaderRef = useRef(null);
  const columnOrderRef = useRef(columnOrder);

  useEffect(() => {
    columnOrderRef.current = columnOrder;
  }, [columnOrder]);

  const columnsCount = columnOrder?.length ?? 0;
  const pinnedColumnsKey = pinnedColumns.join(",");

  useEffect(() => {
    const container = containerRef.current;
    if (columnsCount === 0 || !container) return;
    let observer = null;

    const getColumnIndex = (field) => {
      return columnOrderRef.current?.indexOf(field) ?? -1;
    };
    const computeSlotFromHeader = (target, clientX) => {
      const targetField = target.getAttribute("data-field") ?? "";
      const targetIndex = getColumnIndex(targetField);
      if (targetIndex === -1) return null;
      const startIndex = dragStartIndexRef.current;
      if (targetIndex === startIndex) return null;
      const rect = target.getBoundingClientRect();
      const hoveredSide =
        clientX < rect.left + rect.width / 2 ? "left" : "right";
      const dragDirection = targetIndex < startIndex ? "left" : "right";
      const isOverFarSide = dragDirection === hoveredSide;
      const indicatorParentIndex = isOverFarSide
        ? targetIndex
        : targetIndex + (dragDirection === "left" ? 1 : -1);
      if (indicatorParentIndex === startIndex) return null;
      const cols = columnOrderRef.current;
      const slotIndex =
        dragDirection === "left"
          ? indicatorParentIndex
          : indicatorParentIndex + 1;
      const indicatorField = cols?.[indicatorParentIndex] ?? targetField;
      return { slotIndex, indicatorField, side: dragDirection };
    };
    const isColumnHeader = (el) => {
      return (
        el.classList.contains("MuiDataGrid-columnHeader") &&
        el.hasAttribute("data-field")
      );
    };
    const getHeaderElement = (field) => {
      return container.querySelector(
        `.MuiDataGrid-columnHeader[data-field="${field}"]`
      );
    };

    const clearIndicator = () => {
      if (indicatorParentElementRef.current) {
        indicatorParentElementRef.current.classList.remove(
          INDICATOR_CLASS_LEFT,
          INDICATOR_CLASS_RIGHT
        );
        indicatorParentElementRef.current = null;
      }
      slotIndexRef.current = -1;
    };
    const showIndicator = (field, side) => {
      if (
        indicatorParentElementRef.current?.getAttribute("data-field") !== field
      ) {
        clearIndicator();
      }
      const el = getHeaderElement(field);
      if (!el) return;
      indicatorParentElementRef.current = el;
      el.classList.remove(INDICATOR_CLASS_LEFT, INDICATOR_CLASS_RIGHT);
      el.classList.add(
        side === "left" ? INDICATOR_CLASS_LEFT : INDICATOR_CLASS_RIGHT
      );
    };

    let autoScrollRafId = null;
    let autoScrollDirection = 0;

    const getScrollContainer = () =>
      container.querySelector(".MuiDataGrid-virtualScroller");

    const stopAutoScroll = () => {
      autoScrollDirection = 0;
      if (autoScrollRafId !== null) {
        cancelAnimationFrame(autoScrollRafId);
        autoScrollRafId = null;
      }
    };

    const startAutoScroll = (direction) => {
      if (autoScrollDirection === direction) return;
      stopAutoScroll();
      autoScrollDirection = direction;
      const tick = () => {
        const scroller = getScrollContainer();
        if (!scroller || autoScrollDirection === 0) return;
        scroller.scrollLeft += autoScrollDirection * SCROLL_SPEED;
        autoScrollRafId = requestAnimationFrame(tick);
      };
      autoScrollRafId = requestAnimationFrame(tick);
    };

    const updateAutoScroll = (clientX) => {
      const scroller = getScrollContainer();
      if (!scroller) return;
      const rect = scroller.getBoundingClientRect();
      if (clientX < rect.left + SCROLL_EDGE_ZONE && scroller.scrollLeft > 0) {
        startAutoScroll(-1);
      } else if (
        clientX > rect.right - SCROLL_EDGE_ZONE &&
        scroller.scrollLeft + scroller.clientWidth < scroller.scrollWidth
      ) {
        startAutoScroll(1);
      } else {
        stopAutoScroll();
      }
    };

    const handleDragStart = (e) => {
      const target = e.currentTarget;
      const field = target.getAttribute("data-field");
      if (!field) return;
      const index = getColumnIndex(field);
      if (index === -1) return;
      dragStartIndexRef.current = index;
      e.dataTransfer?.setData("text/plain", field);
      target.style.opacity = "0.5";
      target.style.cursor = "grabbing";
    };
    const handleDragOver = (e) => {
      e.preventDefault();
      updateAutoScroll(e.clientX);
      const target = e.currentTarget;
      const result = computeSlotFromHeader(target, e.clientX);
      if (!result) {
        clearIndicator();
        return;
      }
      const { slotIndex, indicatorField, side } = result;
      slotIndexRef.current = slotIndex;
      showIndicator(indicatorField, side);
    };
    const handleDragLeave = (e) => {
      const target = e.currentTarget;
      const relatedTarget = e.relatedTarget;
      if (!target.contains(relatedTarget)) {
        clearIndicator();
      }
    };
    const handleDrop = (e) => {
      e.preventDefault();
      stopAutoScroll();
      performReorder();
      clearIndicator();
      dragStartIndexRef.current = -1;
    };
    const handleDragEnd = (e) => {
      const target = e.currentTarget;
      target.style.opacity = "";
      target.style.cursor = "";
      stopAutoScroll();
      clearIndicator();
      dragStartIndexRef.current = -1;
    };

    const performReorder = () => {
      const draggedIndex = dragStartIndexRef.current;
      const slotIndex = slotIndexRef.current;
      const cols = columnOrderRef.current;
      if (draggedIndex === -1 || slotIndex === -1 || !cols) return;
      const draggedField = cols[draggedIndex];
      if (draggedField) {
        const { newOrder, newPinned } = computeReorder(
          cols,
          draggedIndex,
          slotIndex,
          pinnedColumns
        );
        setPinnedColumns(newPinned);
        onPinnedColumnsChange(newPinned, pinnedColumns);
        onReorder(newOrder);
      }
    };

    const cleanupDoubleClickDrag = () => {
      stopAutoScroll();
      const header = doubleClickHeaderRef.current;
      if (header) {
        header.style.opacity = "";
        header.style.cursor = "";
        doubleClickHeaderRef.current = null;
      }
      clearIndicator();
      dragStartIndexRef.current = -1;
      doubleClickDragModeRef.current = false;
    };

    const handleDoubleClick = (e) => {
      const target = e.currentTarget;
      const field = target.getAttribute("data-field");
      if (!field) return;
      const index = getColumnIndex(field);
      if (index === -1) return;
      dragStartIndexRef.current = index;
      doubleClickDragModeRef.current = true;
      doubleClickHeaderRef.current = target;
      target.style.opacity = "0.5";
      target.style.cursor = "grabbing";

      const onDocMouseMove = (moveEvent) => {
        if (!doubleClickDragModeRef.current) return;
        updateAutoScroll(moveEvent.clientX);
        const under = document.elementFromPoint(
          moveEvent.clientX,
          moveEvent.clientY
        );
        const header = under?.closest?.(
          ".MuiDataGrid-columnHeader[data-field]"
        );
        if (!header || !container.contains(header)) {
          clearIndicator();
          return;
        }
        const result = computeSlotFromHeader(header, moveEvent.clientX);
        if (!result) {
          clearIndicator();
          return;
        }
        const { slotIndex, indicatorField, side } = result;
        slotIndexRef.current = slotIndex;
        showIndicator(indicatorField, side);
      };

      const onDocMouseUp = () => {
        if (!doubleClickDragModeRef.current) return;
        performReorder();
        document.removeEventListener("mousemove", onDocMouseMove, true);
        document.removeEventListener("mouseup", onDocMouseUp, true);
        cleanupDoubleClickDrag();
      };

      document.addEventListener("mousemove", onDocMouseMove, true);
      document.addEventListener("mouseup", onDocMouseUp, true);
    };

    const attachToHeader = (el) => {
      if (el[DND_ATTACHED_KEY]) return;
      const listeners = [
        ["dragstart", handleDragStart],
        ["dragover", handleDragOver],
        ["dragleave", handleDragLeave],
        ["drop", handleDrop],
        ["dragend", handleDragEnd],
        ["dblclick", handleDoubleClick],
      ];
      listeners.forEach(([event, fn]) => el.addEventListener(event, fn));
      el.draggable = true;
      el.style.cursor = "grab";
      el[DND_ATTACHED_KEY] = listeners;
    };
    const processNode = (node) => {
      if (!(node instanceof HTMLElement)) return;
      if (isColumnHeader(node)) {
        attachToHeader(node);
      }
      const headers = node.getElementsByClassName("MuiDataGrid-columnHeader");
      for (let i = 0; i < headers.length; i++) {
        const header = headers[i];
        if (header.hasAttribute("data-field")) {
          attachToHeader(header);
        }
      }
    };

    const existingHeaders = container.querySelectorAll(COLUMN_HEADER_SELECTOR);
    existingHeaders.forEach((header) => attachToHeader(header));

    observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach(processNode);
      }
    });
    observer.observe(container, {
      childList: true,
      subtree: true,
    });
    return () => {
      stopAutoScroll();
      if (doubleClickHeaderRef.current) {
        doubleClickHeaderRef.current.style.opacity = "";
        doubleClickHeaderRef.current.style.cursor = "";
        doubleClickHeaderRef.current = null;
      }
      doubleClickDragModeRef.current = false;
      clearIndicator();
      observer?.disconnect();
      const headers = container.querySelectorAll(COLUMN_HEADER_SELECTOR);
      headers.forEach((header) => {
        const listeners = header[DND_ATTACHED_KEY];
        if (listeners) {
          listeners.forEach(([event, fn]) =>
            header.removeEventListener(event, fn)
          );
        }
        delete header[DND_ATTACHED_KEY];
      });
    };
  }, [
    containerRef,
    columnsCount,
    pinnedColumnsKey,
    onReorder,
    setPinnedColumns,
    onPinnedColumnsChange,
    pinnedColumns,
  ]);
};
export default useColumnDragDrop;
